/**
 * @fileoverivew a simple method to do pub/sub to mixin or as a standalone utility
 */

/** describes an event type that bears arbitrary data */
type PayloadSubscriptionEvent<T = string, P = any> = { type: T; payload: P };

/** describes the types of events that are subscribable */
type SubscriptionEvent<PayloadType = any> = string | PayloadSubscriptionEvent<string, PayloadType>;

/** type utility to extract the string type or from the `type` property */
type ExtractTypeNames<EventName> = EventName extends PayloadSubscriptionEvent ? EventName["type"] : EventName;

/** type utility to extract the payload of the type, if it is defined */
type ExtractPayloadType<T, EventName> =
  EventName extends PayloadSubscriptionEvent<T> ? EventName["payload"] : never;

const EMPTY_SET = new Set<(args?: any) => void>();

/**
 * An encapsulation of subscription and publish behaviors
 */
export class Subscribable<EventName = SubscriptionEvent> {
  /**
   * member field to memoize the listeners encapsulated by this `Subscribable` instance
   */
  protected subscriptions: Map<ExtractTypeNames<EventName>, Set<() => void>>;

  /**
   * Constructs an instance of subscribable with optional type argument to define the
   * the types of events the instance will publish / to which clients can subscribe
   *
   * @example
   * const subscribable = new Subscribable<"foo" | "bar" | "baz">();
   */
  constructor() {
    this.subscriptions = new Map();
  }

  /**
   * subscribes a `listener` function to the given `eventName`
   *
   * @example
   * const listener = () => { console.log("[foo invoked]") }
   * subscribable.subscribe("foo", listener);
   */
  subscribe<T extends ExtractTypeNames<EventName>>(
    eventName: T,
    listener: (payload?: ExtractPayloadType<T, EventName>) => void
  ) {
    if (!this.subscriptions.has(eventName)) {
      this.subscriptions.set(eventName, new Set([listener]));
    } else {
      this.subscriptions.get(eventName)?.add(listener);
    }
  }

  /**
   * publishes an event to invoke the subscriptions for the given `eventName`
   *
   * @example
   * subscribable.publish("bar");
   */
  publish<T extends ExtractTypeNames<EventName>>(eventName: T, data?: ExtractPayloadType<T, EventName>) {
    const listeners = this.subscriptions.get(eventName) || EMPTY_SET;

    for (const listener of [...listeners]) {
      listener(data);
    }
  }

  /**
   * unsubscribes the given `listener` from the given `eventName`
   *
   * @note this is a no-op if `listener` is not subscribed
   *
   * @example
   * subscribable.unsubscribe("foo", listener);
   */
  unsubscribe<T extends ExtractTypeNames<EventName>>(eventName: T, listener: () => void) {
    const listeners = this.subscriptions.get(eventName) || EMPTY_SET;

    listeners.delete(listener);
  }

  /**
   * unsuscribes all events, or optionally, just for a class of subscriptions
   * @example
   * subscribable.unsubscribleAll();
   *
   * @example
   * subscribable.unsubscribleAll("baz");
   */
  unsubscribeAll(eventName?: ExtractTypeNames<EventName>) {
    if (eventName) {
      this.subscriptions.delete(eventName);
    } else {
      this.subscriptions.clear();
    }
  }
}
